import React, { useEffect, useCallback, useRef, useState, useLayoutEffect } from 'react'
import { Box, Loader, UnstyledButton } from '@mantine/core'
import { AppButton, AppText } from '../app/common/ui'
/** MobX reactivity: `mobx-react-lite@3` provides `observer`; there is no `useObserver` in this version. */
import { observer } from 'mobx-react-lite'
import type EditorStore from '../app/stores/EditorStore'
import type { ShootMediaType } from '../app/stores/EditorStore'
import type { Textbox } from '../app/models/shootTextbox'
import { ShootsTextboxEditor } from './ShootsTextboxEditor'
import { TextboxStylingToolbar } from './TextboxStylingToolbar'
import {
  drawNormalizedTextboxes,
  getObjectFitContainRect,
  normalizedRectToCssPercent,
  SHOOTS_CLOSE_BTN_PX,
  SHOOTS_CLOSE_PAD_PX
} from '../app/utils/shootsCoords'

const DRAG_THRESHOLD_PX = 6

/**
 * Normalized-space pinch resize bounds (fractions of media width/height).
 * Min prevents invisible boxes; max prevents exceeding media bounds.
 */
const RESIZE_MIN_WIDTH_NORM = 0.05
const RESIZE_MAX_WIDTH_NORM = 1.0
const RESIZE_MIN_HEIGHT_NORM = 0.02

/** Default frame aspect before intrinsic dimensions are known (reduces layout jump). */
const FALLBACK_MEDIA_ASPECT = 16 / 9

function clamp (n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n))
}

function clampTextboxPosition (tb: Textbox, x: number, y: number): { x: number; y: number } {
  const maxX = Math.max(0, 1 - tb.width)
  const maxY = Math.max(0, 1 - tb.height)
  return {
    x: clamp(x, 0, maxX),
    y: clamp(y, 0, maxY)
  }
}

function clampTextboxFrame (
  width: number,
  height: number,
  cx: number,
  cy: number
): Pick<Textbox, 'x' | 'y' | 'width' | 'height'> {
  const w = clamp(width, RESIZE_MIN_WIDTH_NORM, RESIZE_MAX_WIDTH_NORM)
  let h = Math.max(RESIZE_MIN_HEIGHT_NORM, height)
  h = Math.min(h, 1)
  let x = cx - w / 2
  let y = cy - h / 2
  x = clamp(x, 0, Math.max(0, 1 - w))
  y = clamp(y, 0, Math.max(0, 1 - h))
  return { x, y, width: w, height: h }
}

/** Map pointer position to normalized 0–1 coordinates over the canvas CSS box. */
function displayToNormalized (clientX: number, clientY: number, rect: DOMRectReadOnly): { nx: number; ny: number } {
  const nx = (clientX - rect.left) / rect.width
  const ny = (clientY - rect.top) / rect.height
  return { nx, ny }
}

function pointInTextboxBody (nx: number, ny: number, tb: Textbox): boolean {
  return nx >= tb.x && nx <= tb.x + tb.width && ny >= tb.y && ny <= tb.y + tb.height
}

function hitTestAt (nx: number, ny: number, textboxes: Textbox[]): { kind: 'body' | 'empty'; id?: string } {
  for (let i = textboxes.length - 1; i >= 0; i--) {
    const tb = textboxes[i]
    if (!pointInTextboxBody(nx, ny, tb)) continue
    return { kind: 'body', id: tb.id }
  }
  return { kind: 'empty' }
}

function drawCanvas (
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  textboxes: Textbox[],
  editingId: string | null
) {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
  const pixW = Math.max(1, Math.floor(cssW * dpr))
  const pixH = Math.max(1, Math.floor(cssH * dpr))
  if (ctx.canvas.width !== pixW || ctx.canvas.height !== pixH) {
    ctx.canvas.width = pixW
    ctx.canvas.height = pixH
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, pixW, pixH)
  ctx.scale(dpr, dpr)

  drawNormalizedTextboxes(ctx, textboxes, cssW, cssH, {
    editingTextboxId: editingId,
    showEditorChrome: true
  })
}

export interface ShootsEditorProps {
  photoUrl: string;
  file: File;
  mediaType: ShootMediaType;
  editorStore: EditorStore;
  onBack: () => void;
  onGoToDetails: () => void;
}

type PointerDownState =
  | {
      kind: 'body';
      id: string;
      clientX0: number;
      clientY0: number;
      tbX0: number;
      tbY0: number;
      rect: DOMRectReadOnly;
    }
  | { kind: 'empty'; nx: number; ny: number; clientX0: number; clientY0: number; dismissedEdit: boolean };

type PinchState = {
  tbId: string;
  startDistPx: number;
  w0: number;
  h0: number;
  cx: number;
  cy: number;
};

/** Video editor: 3 full plays then hold last frame; tap empty canvas to replay (battery-heavy, intentional). */
type VideoPlayState = { loopCount: number; isFrozen: boolean };

const ShootsEditor = observer(function ShootsEditor ({
  photoUrl,
  file,
  mediaType,
  editorStore,
  onBack,
  onGoToDetails
}: ShootsEditorProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const editInputRef = useRef<HTMLInputElement>(null)
  /** Real w/h set by ResizeObserver; zeros until first layout (no legacy 320×logical guess). */
  const [layoutCss, setLayoutCss] = useState({ w: 0, h: 0 })
  const [imgError, setImgError] = useState<string | null>(null)
  const [editingTextboxId, setEditingTextboxId] = useState<string | null>(null)
  const [playState, setPlayState] = useState<VideoPlayState>({ loopCount: 0, isFrozen: false })

  const downRef = useRef<PointerDownState | null>(null)
  const dragMovedRef = useRef(false)
  const activePointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())
  const pinchRef = useRef<PinchState | null>(null)
  const [canvasCursor, setCanvasCursor] = useState<'crosshair' | 'move' | 'grab' | 'grabbing'>('crosshair')

  const frameAspect = editorStore.mediaAspect ?? FALLBACK_MEDIA_ASPECT
  const dimensionsReady = editorStore.mediaAspect != null

  useEffect(() => {
    setImgError(null)
  }, [photoUrl])

  useEffect(() => {
    editorStore.beginShootEdit(file, photoUrl, mediaType)
  }, [editorStore, file, photoUrl, mediaType])

  /** New clip / reopen editor: restart video loop counter. */
  useEffect(() => {
    if (mediaType !== 'video') return
    setPlayState({ loopCount: 0, isFrozen: false })
  }, [mediaType, photoUrl])

  /**
   * Autoplay muted + count `ended` for 3 cycles then freeze on last frame.
   * `loop` stays false so `ended` fires every playthrough (with `loop=true`, many UAs never emit `ended`).
   */
  useEffect(() => {
    if (mediaType !== 'video') return
    const video = videoRef.current
    if (!video) return

    video.muted = true
    video.autoplay = true
    video.loop = false

    const onEnded = () => {
      setPlayState((prev) => {
        const nextCount = prev.loopCount + 1
        if (nextCount >= 3) {
          requestAnimationFrame(() => {
            const v = videoRef.current
            if (v) {
              v.pause()
            }
          })
          return { loopCount: nextCount, isFrozen: true }
        }
        requestAnimationFrame(() => {
          const v = videoRef.current
          if (v) {
            v.currentTime = 0
            void v.play()
          }
        })
        return { loopCount: nextCount, isFrozen: false }
      })
    }

    video.addEventListener('ended', onEnded)
    void video.play().catch(() => undefined)

    return () => {
      video.removeEventListener('ended', onEnded)
    }
  }, [mediaType, photoUrl])

  /** Size the media frame to the largest rect with `frameAspect` that fits the viewport area. */
  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const update = () => {
      const cr = el.getBoundingClientRect()
      const aw = cr.width
      const ah = cr.height
      const aspect = editorStore.mediaAspect ?? FALLBACK_MEDIA_ASPECT
      const { w, h } = getObjectFitContainRect(aw, ah, aspect)
      const fw = w > 1 ? w : Math.max(120, aw * 0.95)
      const fh = h > 1 ? h : Math.max(120, fw / Math.max(1e-6, aspect))
      setLayoutCss({ w: fw, h: fh })
    }
    const ro = new ResizeObserver(() => update())
    ro.observe(el)
    update()
    return () => ro.disconnect()
  }, [editorStore.mediaAspect])

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    drawCanvas(ctx, layoutCss.w, layoutCss.h, editorStore.textboxes, editingTextboxId)
  }, [editorStore.textboxes, editingTextboxId, layoutCss.w, layoutCss.h])

  const finishEditing = useCallback(() => {
    const id = editingTextboxId
    if (!id) return
    const input = editInputRef.current
    if (input) {
      editorStore.updateTextbox(id, { text: input.value })
    }
    setEditingTextboxId(null)
  }, [editingTextboxId, editorStore])

  useEffect(() => {
    if (editingTextboxId) {
      const t = requestAnimationFrame(() => {
        editInputRef.current?.focus()
        editInputRef.current?.select()
      })
      return () => cancelAnimationFrame(t)
    }
  }, [editingTextboxId])

  const handleNext = useCallback(() => {
    finishEditing()
    onGoToDetails()
  }, [finishEditing, onGoToDetails])

  const handleBack = useCallback(() => {
    finishEditing()
    onBack()
  }, [onBack, finishEditing])

  const clearPointerSession = useCallback(() => {
    downRef.current = null
    dragMovedRef.current = false
  }, [])

  const updateHoverCursor = useCallback(
    (clientX: number, clientY: number, rect: DOMRectReadOnly) => {
      if (editorStore.isPosting) return
      const { nx, ny } = displayToNormalized(clientX, clientY, rect)
      const hit = hitTestAt(nx, ny, editorStore.textboxes)
      setCanvasCursor(hit.kind === 'body' ? 'move' : 'crosshair')
    },
    [editorStore.isPosting, editorStore.textboxes]
  )

  const releaseAllPointerCapture = (canvas: HTMLCanvasElement) => {
    for (const pid of activePointersRef.current.keys()) {
      if (canvas.hasPointerCapture(pid)) {
        try {
          canvas.releasePointerCapture(pid)
        } catch {
          /* ignore */
        }
      }
    }
  }

  const handleCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (editorStore.isPosting) return

    const canvas = canvasRef.current
    if (!canvas) return
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (activePointersRef.current.size === 2) {
      if (editorStore.selectedTextboxId) {
        if (editingTextboxId) {
          finishEditing()
        }
        const tb = editorStore.textboxes.find((t) => t.id === editorStore.selectedTextboxId)
        const pts = [...activePointersRef.current.values()]
        if (tb && pts.length === 2) {
          const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y)
          const startDistPx = Math.max(8, dist)
          pinchRef.current = {
            tbId: tb.id,
            startDistPx,
            w0: tb.width,
            h0: tb.height,
            cx: tb.x + tb.width / 2,
            cy: tb.y + tb.height / 2
          }
          releaseAllPointerCapture(canvas)
          downRef.current = null
          dragMovedRef.current = false
          setCanvasCursor('grabbing')
        }
        return
      }
      activePointersRef.current.delete(e.pointerId)
      return
    }

    const rect = canvas.getBoundingClientRect()
    const { nx, ny } = displayToNormalized(e.clientX, e.clientY, rect)

    const hadEditing = editingTextboxId !== null
    let hit = hitTestAt(nx, ny, editorStore.textboxes)

    if (editingTextboxId) {
      const tappingSameEditor = hit.kind === 'body' && hit.id === editingTextboxId
      if (!tappingSameEditor) {
        finishEditing()
        hit = hitTestAt(nx, ny, editorStore.textboxes)
      }
    }

    dragMovedRef.current = false

    if (hit.kind === 'body' && hit.id) {
      const tb = editorStore.textboxes.find((t) => t.id === hit.id)
      if (!tb) return
      editorStore.selectTextbox(hit.id)
      downRef.current = {
        kind: 'body',
        id: hit.id,
        clientX0: e.clientX,
        clientY0: e.clientY,
        tbX0: tb.x,
        tbY0: tb.y,
        rect
      }
      canvas.setPointerCapture(e.pointerId)
      return
    }

    const dismissedEdit = hadEditing && hit.kind === 'empty'
    downRef.current = {
      kind: 'empty',
      nx,
      ny,
      clientX0: e.clientX,
      clientY0: e.clientY,
      dismissedEdit
    }
    canvas.setPointerCapture(e.pointerId)
  }

  const handleCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (activePointersRef.current.has(e.pointerId)) {
      activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }

    const pinch = pinchRef.current
    if (pinch) {
      const pts = [...activePointersRef.current.values()]
      if (pts.length >= 2) {
        const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y)
        const scale = Math.max(0.15, dist / pinch.startDistPx)
        let newW = pinch.w0 * scale
        newW = clamp(newW, RESIZE_MIN_WIDTH_NORM, RESIZE_MAX_WIDTH_NORM)
        const tb = editorStore.textboxes.find((t) => t.id === pinch.tbId)
        if (tb) {
          const minH = Math.max(RESIZE_MIN_HEIGHT_NORM, Math.min(1, tb.fontSize * 1.25))
          const newH = Math.max(minH, pinch.h0 * (newW / Math.max(1e-9, pinch.w0)))
          editorStore.updateTextbox(pinch.tbId, clampTextboxFrame(newW, newH, pinch.cx, pinch.cy))
        }
      }
      setCanvasCursor('grabbing')
      return
    }

    const d = downRef.current
    if (!d) {
      if (e.buttons === 0) {
        const rect = canvas.getBoundingClientRect()
        updateHoverCursor(e.clientX, e.clientY, rect)
      }
      return
    }

    const dx = e.clientX - d.clientX0
    const dy = e.clientY - d.clientY0
    if (dx * dx + dy * dy > DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
      dragMovedRef.current = true
      setCanvasCursor('grabbing')
    }

    if (d.kind === 'body' && dragMovedRef.current) {
      const dnx = dx / d.rect.width
      const dny = dy / d.rect.height
      const tb = editorStore.textboxes.find((t) => t.id === d.id)
      if (tb) {
        const { x, y } = clampTextboxPosition(tb, d.tbX0 + dnx, d.tbY0 + dny)
        editorStore.updateTextbox(d.id, { x, y })
      }
    }
  }

  const handleCanvasPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (canvas?.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId)
    }

    activePointersRef.current.delete(e.pointerId)
    if (pinchRef.current && activePointersRef.current.size < 2) {
      pinchRef.current = null
    }

    const d = downRef.current
    if (!d) {
      if (canvas && activePointersRef.current.size === 0) {
        const rect = canvas.getBoundingClientRect()
        updateHoverCursor(e.clientX, e.clientY, rect)
      }
      return
    }

    const moved = dragMovedRef.current

    if (d.kind === 'body' && d.id && moved) {
      const tb = editorStore.textboxes.find((t) => t.id === d.id)
      if (tb) {
        const { x, y } = clampTextboxPosition(tb, tb.x, tb.y)
        if (x !== tb.x || y !== tb.y) {
          editorStore.updateTextbox(d.id, { x, y })
        }
      }
    }

    if (d.kind === 'body' && d.id) {
      if (!moved) {
        setEditingTextboxId(d.id)
      }
    } else if (d.kind === 'empty') {
      if (!moved) {
        if (mediaType === 'video' && playState.isFrozen) {
          setPlayState({ loopCount: 0, isFrozen: false })
          const v = videoRef.current
          if (v) {
            v.currentTime = 0
            void v.play()
          }
          clearPointerSession()
          if (canvas) {
            const rect = canvas.getBoundingClientRect()
            updateHoverCursor(e.clientX, e.clientY, rect)
          }
          return
        }
        if (d.dismissedEdit) {
          clearPointerSession()
          if (canvas) {
            const rect = canvas.getBoundingClientRect()
            updateHoverCursor(e.clientX, e.clientY, rect)
          }
          return
        }
        if (editorStore.selectedTextboxId) {
          editorStore.deselectTextbox()
        } else {
          const created = editorStore.createTextbox(d.nx, d.ny)
          setEditingTextboxId(created.id)
        }
      }
    }

    clearPointerSession()
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      updateHoverCursor(e.clientX, e.clientY, rect)
    }
  }

  const handleCanvasPointerCancel = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (canvas?.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId)
    }
    activePointersRef.current.delete(e.pointerId)
    if (pinchRef.current && activePointersRef.current.size < 2) {
      pinchRef.current = null
    }
    clearPointerSession()
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      updateHoverCursor(e.clientX, e.clientY, rect)
    }
  }

  const handleInputBlur = useCallback(() => {
    finishEditing()
  }, [finishEditing])

  const deleteTextboxByClose = useCallback(
    (id: string) => {
      if (editingTextboxId === id) {
        setEditingTextboxId(null)
      }
      editorStore.deleteTextbox(id)
    },
    [editingTextboxId, editorStore]
  )

  const editingTb = editingTextboxId ? editorStore.textboxes.find((t) => t.id === editingTextboxId) : null
  const editFontPx = editingTb ? Math.max(12, editingTb.fontSize * layoutCss.h) : 14
  const selectedTb = editorStore.selectedTextbox

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          flex: 1,
          minHeight: 0,
          maxHeight: 'calc(100dvh - 96px)',
          backgroundColor: 'var(--gs-bg-2)',
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid var(--gs-border)',
          paddingBottom: editorStore.selectedTextbox ? 100 : 0,
          transition: 'padding-bottom 0.15s ease'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderBottom: '1px solid var(--gs-border)',
            flexShrink: 0
          }}
        >
          <AppButton variant="subtle" size="sm" compact onClick={handleBack} sx={{ fontWeight: 600 }}>
            &lt; Back
          </AppButton>
          <AppButton
            variant="filled"
            color="orange"
            size="sm"
            radius="xl"
            disabled={!editorStore.canPost || !dimensionsReady}
            onClick={handleNext}
            px="lg"
          >
            Next
          </AppButton>
        </Box>

        <Box sx={{ padding: 12, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {imgError ? (
            <AppText size="sm" c="red">
              {imgError}
            </AppText>
          ) : (
            <Box
              ref={viewportRef}
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
                borderRadius: 6
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: layoutCss.w,
                  height: layoutCss.h,
                  maxWidth: '100%',
                  maxHeight: '100%',
                  overflow: 'hidden',
                  touchAction: 'none'
                }}
              >
                {!dimensionsReady && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      zIndex: 20
                    }}
                  >
                    <Loader size="sm" color="gray" />
                  </Box>
                )}
                {mediaType === 'photo'
                  ? (
                  <img
                    src={photoUrl}
                    alt=""
                    onLoad={(ev) => {
                      setImgError(null)
                      const im = ev.currentTarget
                      if (im.naturalWidth > 0 && im.naturalHeight > 0) {
                        editorStore.setMediaIntrinsicDimensions(im.naturalWidth, im.naturalHeight)
                      }
                    }}
                    onError={() => setImgError('Could not load image. Check the file or try another photo.')}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  />
                    )
                  : (
                  <video
                    ref={videoRef}
                    src={photoUrl}
                    autoPlay
                    muted
                    playsInline
                    preload="auto"
                    onLoadedMetadata={(ev) => {
                      const v = ev.currentTarget
                      if (v.videoWidth > 0 && v.videoHeight > 0) {
                        editorStore.setMediaIntrinsicDimensions(v.videoWidth, v.videoHeight)
                      }
                    }}
                    onLoadedData={() => setImgError(null)}
                    onError={() => setImgError('Could not load video. Check the file or try another clip.')}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  />
                    )}
                <canvas
                  ref={canvasRef}
                  onPointerDown={handleCanvasPointerDown}
                  onPointerMove={handleCanvasPointerMove}
                  onPointerLeave={(ev) => {
                    if (!downRef.current && !pinchRef.current && ev.buttons === 0) {
                      setCanvasCursor('crosshair')
                    }
                  }}
                  onPointerUp={handleCanvasPointerUp}
                  onPointerCancel={handleCanvasPointerCancel}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    touchAction: 'none',
                    cursor: canvasCursor
                  }}
                />
                {editingTb && (
                  <ShootsTextboxEditor
                    key={editingTb.id}
                    textbox={editingTb}
                    editorStore={editorStore}
                    inputRef={editInputRef}
                    fontSizePx={editFontPx}
                    onBlur={handleInputBlur}
                  />
                )}
                {selectedTb && dimensionsReady && layoutCss.w > 0 && layoutCss.h > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      zIndex: 40
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        ...normalizedRectToCssPercent(selectedTb),
                        pointerEvents: 'none',
                        transform: `rotate(${selectedTb.rotation}deg)`,
                        transformOrigin: 'center center'
                      }}
                    >
                      <UnstyledButton
                        type="button"
                        aria-label="Delete textbox"
                        onPointerDown={(ev) => {
                          // Block focus moving to this control before pointerup; otherwise the overlay
                          // input blurs, finishEditing runs, selection clears, and this node unmounts.
                          ev.stopPropagation()
                          ev.preventDefault()
                        }}
                        onPointerUp={(ev) => {
                          ev.stopPropagation()
                          if (ev.button !== 0) return
                          deleteTextboxByClose(selectedTb.id)
                        }}
                        onClick={(ev) => {
                          ev.stopPropagation()
                          deleteTextboxByClose(selectedTb.id)
                        }}
                        sx={{
                          position: 'absolute',
                          top: SHOOTS_CLOSE_PAD_PX,
                          right: SHOOTS_CLOSE_PAD_PX,
                          width: SHOOTS_CLOSE_BTN_PX,
                          height: SHOOTS_CLOSE_BTN_PX,
                          minWidth: SHOOTS_CLOSE_BTN_PX,
                          padding: 0,
                          pointerEvents: 'auto',
                          backgroundColor: 'rgba(0,0,0,0.55)',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.35)',
                          borderRadius: 2,
                          fontSize: 16,
                          lineHeight: `${SHOOTS_CLOSE_BTN_PX}px`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          touchAction: 'manipulation',
                          userSelect: 'none',
                          cursor: 'pointer',
                          '&:focus-visible': {
                            outline: '2px solid var(--mantine-color-orange-6)',
                            outlineOffset: 2
                          }
                        }}
                      >
                        ×
                      </UnstyledButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <TextboxStylingToolbar selectedTextbox={editorStore.selectedTextbox} editorStore={editorStore} />
    </>
  )
})

export default ShootsEditor
